
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LoaderService } from './loader.service';
import { ReferenceService } from './reference.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})
export class HttpServices {


  constructor(
    private _http: HttpClient,
    private userService: UserService,
    private referenceService: ReferenceService,
    private router: Router,
    private loaderService: LoaderService
  ) { }

  private setQueryParams(params: QueryParameter[]): HttpParams {
    let result = new HttpParams();
    if (params) {
      params.forEach(param => result = result.append(param.Key, param.Value));
    }
    return result;
  }

  private setHeaderAuth(token: string, params: { [param: string]: string }): { headers: HttpHeaders, params: { [param: string]: string },
withCredentials: boolean } {
    return {
      headers: new HttpHeaders({ Authorization: token }),
      params: params,
      withCredentials: true
    };
  }

  public get<R>(ApiEndpoint: string, extractData: (ret: any) => void, params?: QueryParameter[]): Observable<R | never> {
    if (!this.userService.isLoggedIn() && !this.userService.isMonitoringMode()) {
      return throwError({ status: 401 })
        .pipe(catchError(m => this.handleError(m, this.router, this.loaderService)));
    }
    const actionUrl = this.referenceService.staticReferences.ApiServer + this.referenceService.staticReferences.ApiVersion + ApiEndpoint;
    const queryParams = this.setQueryParams(params);
    return this._http.get<R>(actionUrl, { params: queryParams, withCredentials: true })
      .pipe(catchError(m => this.handleError(m, this.router, this.loaderService)));
  }

  public getDuo<R>(ApiEndpoint: string, extractData: (ret: any) => void, params?: QueryParameter[]): Observable<R | never> {
    if (!this.userService.isLoggedIn() && !this.userService.isMonitoringMode()) {
      return throwError({ status: 401 })
        .pipe(catchError(m => this.handleError(m, this.router, this.loaderService)));
    }

    const token = this.userService.getAccessToken();
    let requestOptions = {};
    if (token) {
      requestOptions = Object.assign(requestOptions, {
        headers: new HttpHeaders({ Authorization: token }),
        withCredentials: true
      });
    }
    const actionUrl = this.referenceService.staticReferences.ApiDuoServer + this.referenceService.staticReferences.ApiDuoVersion
      + ApiEndpoint;
    return this._http.get<R>(actionUrl, requestOptions).pipe(catchError(m => this.handleError(m, this.router, this.loaderService)));
  }

  public getWithoutAuth<R>(relativeQuery: string, extractData: (ret: any) => void, params?: QueryParameter[]): Observable<R | never> {
    const actionUrl = this.referenceService.staticReferences.ApiServer + this.referenceService.staticReferences.ApiVersion
      + relativeQuery;

    const queryParams = this.setQueryParams(params);

    return this._http.get<R>(actionUrl, { params: queryParams, withCredentials: true })
      .pipe(catchError(m => this.handleErrorWithoutAuth(m, this.router, this.loaderService)));
  }

  public getWithAuthHeader<R>(ApiEndpoint: string, params: { [param: string]: string }): Observable<R> {
    if (!this.userService.isLoggedIn() && !this.userService.isMonitoringMode()) {
      return throwError({ status: 401 })
        .pipe(catchError(m => this.handleError(m, this.router, this.loaderService)));
    }

    const token = this.userService.getAccessToken();
    let requestOptions = {};
    if (token) {
      requestOptions = this.setHeaderAuth(token, params);
    }
    const actionUrl = this.referenceService.staticReferences.ApiServer + this.referenceService.staticReferences.ApiVersion
      + ApiEndpoint;
    return this._http.get<R>(actionUrl, requestOptions)
    .pipe(catchError(m => this.handleErrorWithoutAuth(m, this.router, this.loaderService)));
  }

  private handleError(error: any, router: Router, loaderService: LoaderService) {
    // In a real world app, we might use a remote logging infrastructure
    // We'd also dig deeper into the error to get a better message
    const errMsg = (error.message) ? error.message :
      error.status ? `${error.status} - ${error.statusText}` : 'Server error';

    // 401 error handling: redirect to logout
    if (error.status === 401) {
      console.warn(errMsg);
      this.userService.logout().subscribe({
        error: logoutError => console.error('Logout failed', logoutError)
      });
      return EMPTY; // Stops the chain, completes silently
    }

    console.error(errMsg);
    loaderService.resetCount();
    router.navigate(['/error']);
    return throwError(errMsg);
  }

  private handleErrorWithoutAuth(error: any, router: Router, loaderService: LoaderService) {

    const errMsg = (error.message) ? error.message :
      error.status ? `${error.status} - ${error.statusText}` : 'Server error';
    console.error(errMsg); // log to console instead

    loaderService.resetCount();

    return throwError(error);
  }

  // B: la classe json corrspondante au body
  // R: la classe json corrspondante au retour
  public put<B, R>(apiKey: string, body: B, extractData: (ret: any) => void, params?: QueryParameter[]): Observable<R | never> {
    if (!this.userService.isLoggedIn()) {
      return throwError({ status: 401 })
        .pipe(catchError(m => this.handleError(m, this.router, this.loaderService)));
    }

    const actionUrl = this.referenceService.staticReferences.ApiServer + this.referenceService.staticReferences.ApiVersion
      + this.referenceService.staticReferences.Action[apiKey];

    const queryParams = this.setQueryParams(params);

    return this._http.put<R>(actionUrl, body, { params: queryParams, withCredentials: true })
      .pipe(catchError(m => this.handleError(m, this.router, this.loaderService)));
  }

  public post<B, R>(ApiEndpoint: string, body: B, extractData: (ret: any) => void, params?: QueryParameter[]): Observable<R | never> {
    if (!this.userService.isLoggedIn()) {
      return throwError({ status: 401 })
        .pipe(catchError(m => this.handleError(m, this.router, this.loaderService)));
    }

    const actionUrl = this.referenceService.staticReferences.ApiServer + this.referenceService.staticReferences.ApiVersion
      + ApiEndpoint;

    const queryParams = this.setQueryParams(params);

    return this._http.post<R>(actionUrl, body, { params: queryParams, withCredentials: true })
      .pipe(catchError(m => this.handleError(m, this.router, this.loaderService)));
  }

  public postWithoutAuth<B, R>(relativeQuery: string, body: B, extractData: (ret: any) => void, params?: QueryParameter[])
    : Observable<R | never> {
    const actionUrl = this.referenceService.staticReferences.ApiServer + this.referenceService.staticReferences.ApiVersion
      + relativeQuery;
    return this._http.post<R>(actionUrl, body, { withCredentials: true })
    .pipe(catchError(m => this.handleErrorWithoutAuth(m, this.router, this.loaderService)));
  }

  // T: la classe json corrspondante au body
  // R: la classe json corrspondante au retour
  public delete<R>(apiKey: string, extractData: (ret: any) => void, params?: QueryParameter[]): Observable<R | never> {
    if (!this.userService.isLoggedIn()) {
      return throwError({ status: 401 })
        .pipe(catchError(m => this.handleError(m, this.router, this.loaderService)));
    }

    const actionUrl = this.referenceService.staticReferences.ApiServer + this.referenceService.staticReferences.ApiVersion
      + this.referenceService.staticReferences.Action[apiKey];

    const queryParams = this.setQueryParams(params);

    return this._http.delete<R>(actionUrl, { params: queryParams, withCredentials: true })
      .pipe(catchError(m => this.handleError(m, this.router, this.loaderService)));
  }
}

export class QueryParameter {
  public Key: string;
  public Value: string;
}
